import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api.js";

/**
 * ActiveSessionsBadge — small chip showing how many viewers are currently
 * connected to a device, with a popover listing each one (kind, email,
 * connect time) and a button to disconnect any of them.
 *
 * Used on:
 *   - DevicesPage cards (owner view)
 *   - AdminDevicesPage rows (admin view of any user's device)
 *
 * Polls every 4 seconds while mounted so the count stays roughly live
 * without hammering the backend.
 */
export default function ActiveSessionsBadge({ deviceUuid, compact = false }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const popoverRef = useRef(null);
  const anchorRef = useRef(null);

  async function load() {
    try {
      const r = await api(`/devices/${deviceUuid}/active-sessions`);
      setSessions(r.sessions || []);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [deviceUuid]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function kick(sessionId) {
    if (!confirm("Diese Verbindung trennen?")) return;
    try {
      await api(`/active-sessions/${sessionId}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert("Trennen fehlgeschlagen: " + e.message);
    }
  }

  // Count by kind for the summary
  const counts = sessions.reduce((acc, s) => {
    acc[s.access_kind] = (acc[s.access_kind] || 0) + 1;
    return acc;
  }, {});

  const total = sessions.length;

  // While loading first time, render nothing to avoid flash
  if (loading && total === 0) return null;
  // If no one is connected, render a subtle muted indicator (so the user
  // can still see at a glance "nobody is on this device")
  if (total === 0) {
    return (
      <span
        title="Niemand verbunden"
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 7px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid var(--line)",
          borderRadius: 4,
          fontSize: 10,
          color: "var(--text-faint)",
          fontFamily: "var(--mono)",
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--text-faint)" }} />
        0 connected
      </span>
    );
  }

  return (
    <>
      <button
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        title="Aktive Verbindungen anzeigen"
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "2px 8px",
          background: "rgba(74,222,128,0.10)",
          border: "1px solid rgba(74,222,128,0.35)",
          borderRadius: 4,
          fontSize: 10,
          color: "var(--ok)",
          fontFamily: "var(--mono)",
          cursor: "pointer",
        }}
      >
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: "var(--ok)", boxShadow: "0 0 4px var(--ok)",
        }} />
        {compact ? `${total}` : `${total} connected`}
        {!compact && Object.entries(counts).length > 0 && (
          <span style={{ color: "var(--text-dim)", marginLeft: 2 }}>
            ({Object.entries(counts).map(([k, n]) => `${n}${kindShort(k)}`).join(" ")})
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popoverRef}
          style={{
            position: "absolute",
            zIndex: 100,
            marginTop: 6,
            minWidth: 320,
            maxWidth: 420,
            background: "var(--bg-elev)",
            border: "1px solid var(--line)",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
            padding: 8,
            fontFamily: "var(--mono)",
          }}
        >
          <div style={{
            fontSize: 10, color: "var(--text-dim)",
            letterSpacing: "0.1em", textTransform: "uppercase",
            padding: "4px 6px 8px",
          }}>
            Aktive Verbindungen ({total})
          </div>
          {err && (
            <div style={{ fontSize: 10, color: "var(--err)", padding: "4px 6px" }}>{err}</div>
          )}
          {sessions.map((s) => (
            <SessionRow key={s.session_id} s={s} onKick={() => kick(s.session_id)} />
          ))}
        </div>
      )}
    </>
  );
}

function SessionRow({ s, onKick }) {
  const since = relTime(s.opened_at);
  const kindStyle = kindStyles(s.access_kind);
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 8, padding: "8px 6px",
      borderRadius: 4,
      background: "rgba(255,255,255,0.02)",
      marginBottom: 4,
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{
            padding: "1px 6px",
            background: kindStyle.bg,
            border: `1px solid ${kindStyle.border}`,
            borderRadius: 3,
            fontSize: 9,
            color: kindStyle.fg,
            letterSpacing: "0.08em",
          }}>
            {s.access_kind.toUpperCase()}
          </span>
          <span style={{ fontSize: 11, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis" }}>
            {s.access_kind === "guest"
              ? (s.guest_label || "Guest")
              : (s.viewer?.email || `user #${s.viewer?.id}`)}
          </span>
        </div>
        <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 3 }}>
          seit {since}
          {s.client_ip && <> · {s.client_ip}</>}
          {(s.bytes_in || s.bytes_out) && (
            <> · ↓{kb(s.bytes_in)} ↑{kb(s.bytes_out)}</>
          )}
        </div>
      </div>
      <button
        onClick={onKick}
        title="Verbindung trennen"
        style={{
          background: "transparent",
          border: "1px solid rgba(239,68,68,0.3)",
          color: "var(--err)",
          padding: "3px 9px",
          borderRadius: 3,
          fontSize: 10,
          cursor: "pointer",
          flexShrink: 0,
          fontFamily: "inherit",
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
      >
        Kick
      </button>
    </div>
  );
}

function kindStyles(k) {
  switch (k) {
    case "guest": return { bg: "rgba(234,179,8,0.12)",  border: "rgba(234,179,8,0.4)",  fg: "#facc15" };
    case "share": return { bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.4)", fg: "#c084fc" };
    case "admin": return { bg: "rgba(244,114,182,0.12)", border: "rgba(244,114,182,0.4)", fg: "#f472b6" };
    case "owner":
    default:      return { bg: "rgba(74,222,128,0.10)", border: "rgba(74,222,128,0.35)", fg: "var(--ok)" };
  }
}

function kindShort(k) {
  return { owner: "O", admin: "A", share: "S", guest: "G" }[k] || k[0]?.toUpperCase() || "?";
}

function relTime(ts) {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

function kb(n) {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
